// Financial Data Loader - Dynamic loading of financial rates and configuration
// Supports JSON scraper integration with fallback to hardcoded values
// Last updated: January 2025

import { FinancialConfig } from './FinancialConfig.js';
import { FinancialDataErrorHandler } from './FinancialDataErrorHandler.js';
import { FinancialDataValidator } from './FinancialDataValidator.js';
import { resolveFinancialYearRates } from './resolveFinancialYearRates.js';

export class FinancialDataLoader {
    static _cachedData = null;
    static _lastLoadDate = null;
    static _rawRatesJson = null;
    static _cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    /** Raw financial-rates.json root (includes schedules) for FY-resolved simulation lookups. */
    static getRawRatesJson() {
        return this._rawRatesJson;
    }

    /**
     * Load financial rates and configuration data
     * @param {Date|string} date - Date for which to load rates (defaults to current date)
     * @param {boolean} forceRefresh - Force refresh even if cached data exists
     * @returns {Promise<Object>} Financial configuration object
     */
    static async loadRates(date = new Date(), forceRefresh = false) {
        const targetDate = typeof date === 'string' ? new Date(date) : date;
        const dateKey = targetDate.toISOString().split('T')[0];

        // Check cache first
        if (!forceRefresh && this._cachedData && this._lastLoadDate === dateKey) {
            const cacheAge = Date.now() - (this._cachedData._loadTimestamp || 0);
            if (cacheAge < this._cacheExpiry) {
                return this._cachedData;
            }
        }

        try {
            // Try to load from JSON scraper API
            const jsonData = await this._loadFromAPI(dateKey);
            if (jsonData) {
                const validatedData = this._validateAndMerge(jsonData, targetDate);
                this._cacheData(validatedData, dateKey);
                return validatedData;
            }
        } catch (error) {
            const fallbackData = FinancialDataErrorHandler.createFallbackData('all');
            return FinancialDataErrorHandler.handleError(error, 'API_LOAD', fallbackData).data;
        }

        // Fallback to hardcoded values
        console.info('Using hardcoded financial configuration as fallback');
        const fallbackData = this._createFallbackData(targetDate);
        this._cacheData(fallbackData, dateKey);
        return fallbackData;
    }

    /**
     * Load data from JSON file or scraper API
     * @param {string} dateKey - Date key in YYYY-MM-DD format
     * @returns {Promise<Object|null>} JSON data or null if failed
     */
    static async _loadFromAPI(dateKey) {
        // 1. Try to load from local JSON file first (single source of truth)
        try {
            const localData = await this._loadFromLocalJSON(dateKey);
            if (localData && this._validateAPIResponse(localData)) {
                console.info('Loaded financial rates from local JSON file');
                return localData;
            }
        } catch (error) {
            // Local JSON file not available - handled silently
        }

        // 2. Try JSON scraper API endpoints (when scraper is implemented)
        const apiEndpoints = [
            `/api/financial-rates/${dateKey}`,
            `/api/ato-rates/${dateKey}`,
            `/api/superannuation-rates/${dateKey}`
        ];

        for (const endpoint of apiEndpoints) {
            try {
                const response = await fetch(endpoint, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    timeout: 5000 // 5 second timeout
                });

                if (response.ok) {
                    const data = await response.json();
                    if (this._validateAPIResponse(data)) {
                        console.info(`Loaded financial rates from API: ${endpoint}`);
                        return data;
                    }
                }
            } catch (error) {
                // API endpoint failed - handled silently
                continue;
            }
        }

        return null;
    }

    /**
     * Load data from local JSON file (single source of truth)
     * @returns {Promise<Object|null>} JSON data or null if failed
     */
    static async _loadFromLocalJSON(dateKey = null) {
        try {
            const response = await fetch('/data/financial-rates.json', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                cache: 'no-cache' // Always get fresh data
            });

            if (!response.ok) {
                throw new Error(`Failed to load local JSON: ${response.status}`);
            }

            const data = await response.json();
            this._rawRatesJson = data;
            const asAt = dateKey ? new Date(`${dateKey}T12:00:00.000Z`) : new Date();
            return resolveFinancialYearRates(data, asAt);
        } catch (error) {
            throw new Error(`Local JSON load failed: ${error.message}`);
        }
    }

    /**
     * Validate API response structure
     * @param {Object} data - API response data
     * @returns {boolean} True if valid
     */
    static _validateAPIResponse(data) {
        if (!data || typeof data !== 'object') {
            return false;
        }

        // Check for required top-level properties
        const requiredProperties = [
            'contributionCaps',
            'transferBalanceCap',
            'minimumPensionStandards',
            'smsfFees'
        ];

        return requiredProperties.every(prop => data.hasOwnProperty(prop));
    }

    /**
     * Validate and merge JSON data with hardcoded defaults
     * @param {Object} jsonData - Data from JSON scraper
     * @param {Date} targetDate - Target date for validation
     * @returns {Object} Validated and merged configuration
     */
    static _validateAndMerge(jsonData, targetDate) {
        // Validate the JSON data first
        const validationResult = FinancialDataValidator.validateData(jsonData, jsonData.version || '1.0');
        if (!validationResult.isValid) {
            console.warn('JSON data validation failed:', validationResult.errors);
            // Use fallback data if validation fails
            return this._createFallbackData(targetDate);
        }

        // Check compatibility
        if (validationResult.compatibility === 'incompatible') {
            console.warn('Incompatible data version, using fallback');
            return this._createFallbackData(targetDate);
        }

        // Keep the canonical JSON schema intact. FinancialConfig owns the only
        // JSON-to-runtime mapping; transforming it here previously skipped tax,
        // SAPTO, SG, bring-forward and TSB fields before they reached that mapper.
        return {
            ...jsonData,
            metadata: {
                ...(jsonData.metadata || {}),
                source: 'json_scraper',
                loadDate: targetDate.toISOString(),
                loadTimestamp: Date.now(),
                version: jsonData.version || jsonData.metadata?.financialYear || '1.0',
                financialYear: jsonData.financialYear || jsonData.metadata?.financialYear,
            },
        };
    }

    /**
     * Validate contribution caps data
     * @param {Object} caps - Contribution caps data
     * @returns {Object} Validated caps
     */
    static _validateContributionCaps(caps) {
        const validated = {};
        
        if (typeof caps.concessional === 'number' && caps.concessional > 0) {
            validated.CONCESSIONAL = caps.concessional;
        }
        
        if (typeof caps.nonConcessional === 'number' && caps.nonConcessional > 0) {
            validated.NON_CONCESSIONAL = caps.nonConcessional;
        }
        
        if (typeof caps.totalBalanceThreshold === 'number' && caps.totalBalanceThreshold > 0) {
            validated.TOTAL_BALANCE_THRESHOLD = caps.totalBalanceThreshold;
        }
        
        if (typeof caps.carryForwardYears === 'number' && caps.carryForwardYears > 0) {
            validated.CARRY_FORWARD_YEARS = caps.carryForwardYears;
        }

        if (caps.coContribution && typeof caps.coContribution === 'object') {
            const cc = caps.coContribution;
            validated.CO_CONTRIBUTION = {
                ...FinancialConfig.CONTRIBUTION_CAPS.CO_CONTRIBUTION,
                ...(typeof cc.maxAmount === 'number' && cc.maxAmount > 0 ? { MAX_AMOUNT: cc.maxAmount } : {}),
                ...(typeof cc.minIncomeThreshold === 'number' && cc.minIncomeThreshold > 0
                    ? { MIN_INCOME_THRESHOLD: cc.minIncomeThreshold } : {}),
                ...(typeof cc.maxIncomeThreshold === 'number' && cc.maxIncomeThreshold > 0
                    ? { MAX_INCOME_THRESHOLD: cc.maxIncomeThreshold } : {}),
                ...(typeof cc.matchingRate === 'number' && cc.matchingRate > 0
                    ? { MATCHING_RATE: cc.matchingRate } : {})
            };
        }

        if (caps.listo && typeof caps.listo === 'object') {
            const listo = caps.listo;
            validated.LISTO = {
                ...FinancialConfig.CONTRIBUTION_CAPS.LISTO,
                ...(typeof listo.maxOffset === 'number' && listo.maxOffset > 0
                    ? { MAX_OFFSET: listo.maxOffset } : {}),
                ...(typeof listo.minIncomeThreshold === 'number' && listo.minIncomeThreshold > 0
                    ? { MIN_INCOME_THRESHOLD: listo.minIncomeThreshold } : {}),
                ...(typeof listo.maxIncomeThreshold === 'number' && listo.maxIncomeThreshold > 0
                    ? { MAX_INCOME_THRESHOLD: listo.maxIncomeThreshold } : {}),
                ...(typeof listo.contributionsTaxRate === 'number' && listo.contributionsTaxRate > 0
                    ? { CONTRIBUTIONS_TAX_RATE: listo.contributionsTaxRate } : {})
            };
        }

        return validated;
    }

    /**
     * Validate minimum pension standards data
     * @param {Object} standards - Minimum pension standards data
     * @returns {Object} Validated standards
     */
    static _validateMinimumPensionStandards(standards) {
        const validated = {};
        
        if (standards.withdrawalRates && typeof standards.withdrawalRates === 'object') {
            validated.WITHDRAWAL_RATES = {};
            
            Object.entries(standards.withdrawalRates).forEach(([age, rate]) => {
                const ageNum = parseInt(age);
                const rateNum = parseFloat(rate);
                
                if (ageNum >= 65 && ageNum <= 95 && rateNum > 0 && rateNum <= 0.15) {
                    validated.WITHDRAWAL_RATES[ageNum] = rateNum;
                }
            });
        }
        
        if (typeof standards.maximumWithdrawalRate === 'number' && standards.maximumWithdrawalRate > 0) {
            validated.MAXIMUM_WITHDRAWAL_RATE = standards.maximumWithdrawalRate;
        }

        return validated;
    }

    /**
     * Validate SMSF fees data
     * @param {Object} fees - SMSF fees data
     * @returns {Object} Validated fees
     */
    static _validateSMSFFees(fees) {
        const validated = {};
        
        // Validate trustee fees
        if (fees.trusteeFees) {
            validated.TRUSTEE_FEES = {};
            
            if (fees.trusteeFees.individual) {
                validated.TRUSTEE_FEES.INDIVIDUAL = {
                    ...FinancialConfig.SMSF.FEES.TRUSTEE_FEES.INDIVIDUAL,
                    ...this._validateTrusteeFeeStructure(fees.trusteeFees.individual)
                };
            }
            
            if (fees.trusteeFees.corporate) {
                validated.TRUSTEE_FEES.CORPORATE = {
                    ...FinancialConfig.SMSF.FEES.TRUSTEE_FEES.CORPORATE,
                    ...this._validateTrusteeFeeStructure(fees.trusteeFees.corporate)
                };
            }
        }
        
        // Validate audit costs
        if (fees.auditCosts) {
            validated.AUDIT_COSTS = {
                ...FinancialConfig.SMSF.FEES.AUDIT_COSTS,
                ...this._validateAuditCosts(fees.auditCosts)
            };
        }
        
        // Validate compliance costs
        if (fees.complianceCosts) {
            validated.COMPLIANCE_COSTS = {
                ...FinancialConfig.SMSF.FEES.COMPLIANCE_COSTS,
                ...this._validateComplianceCosts(fees.complianceCosts)
            };
        }

        return validated;
    }

    /**
     * Validate trustee fee structure
     * @param {Object} feeStructure - Trustee fee structure
     * @returns {Object} Validated fee structure
     */
    static _validateTrusteeFeeStructure(feeStructure) {
        const validated = {};
        
        if (typeof feeStructure.fixed === 'number' && feeStructure.fixed >= 0) {
            validated.FIXED = feeStructure.fixed;
        }
        
        if (typeof feeStructure.percentage === 'number' && feeStructure.percentage >= 0 && feeStructure.percentage <= 0.1) {
            validated.PERCENTAGE = feeStructure.percentage;
        }
        
        if (typeof feeStructure.maximum === 'number' && feeStructure.maximum > 0) {
            validated.MAXIMUM = feeStructure.maximum;
        }

        return validated;
    }

    /**
     * Validate audit costs
     * @param {Object} auditCosts - Audit costs data
     * @returns {Object} Validated audit costs
     */
    static _validateAuditCosts(auditCosts) {
        const validated = {};
        
        if (typeof auditCosts.standard === 'number' && auditCosts.standard > 0) {
            validated.STANDARD = auditCosts.standard;
        }
        
        if (typeof auditCosts.complex === 'number' && auditCosts.complex > 0) {
            validated.COMPLEX = auditCosts.complex;
        }
        
        if (typeof auditCosts.minimum === 'number' && auditCosts.minimum > 0) {
            validated.MINIMUM = auditCosts.minimum;
        }
        
        if (typeof auditCosts.maximum === 'number' && auditCosts.maximum > 0) {
            validated.MAXIMUM = auditCosts.maximum;
        }

        return validated;
    }

    /**
     * Validate compliance costs
     * @param {Object} complianceCosts - Compliance costs data
     * @returns {Object} Validated compliance costs
     */
    static _validateComplianceCosts(complianceCosts) {
        const validated = {};
        
        const costFields = ['annualReturn', 'bookkeeping', 'administration', 'softwareLicenses'];
        
        costFields.forEach(field => {
            const upperField = field.toUpperCase();
            if (typeof complianceCosts[field] === 'number' && complianceCosts[field] >= 0) {
                validated[upperField] = complianceCosts[field];
            }
        });

        return validated;
    }

    /**
     * Validate age pension data
     * @param {Object} agePension - Age pension data
     * @returns {Object} Validated age pension data
     */
    static _validateAgePension(agePension) {
        const validated = {};
        
        if (agePension.rates && typeof agePension.rates === 'object') {
            validated.RATES = {};
            
            Object.entries(agePension.rates).forEach(([key, value]) => {
                if (typeof value === 'number' && value >= 0) {
                    validated.RATES[key] = value;
                }
            });
        }
        
        if (agePension.assetThresholds && typeof agePension.assetThresholds === 'object') {
            validated.ASSET_THRESHOLDS = {};
            
            Object.entries(agePension.assetThresholds).forEach(([key, value]) => {
                if (typeof value === 'number' && value >= 0) {
                    validated.ASSET_THRESHOLDS[key] = value;
                }
            });
        }
        
        // Validate Work Bonus parameters
        if (agePension.workBonus && typeof agePension.workBonus === 'object') {
            validated.WORK_BONUS = {};
            
            if (typeof agePension.workBonus.annualExemption === 'number' && agePension.workBonus.annualExemption >= 0) {
                validated.WORK_BONUS.INCOME_FREE_AREA = agePension.workBonus.annualExemption / 26; // Convert annual to fortnightly
                validated.WORK_BONUS.MAX_ACCRUAL = agePension.workBonus.annualExemption; // Annual exemption
            }
            
            if (typeof agePension.workBonus.maxBalance === 'number' && agePension.workBonus.maxBalance >= 0) {
                // Note: maxBalance is the maximum that can accrue, not used in current implementation
                // but included for future reference
            }
            
            if (typeof agePension.workBonus.enabled === 'boolean') {
                validated.WORK_BONUS.ACCRUAL_RATE = agePension.workBonus.enabled ? 1 : 0;
            }
            
            console.info('✓ Work Bonus parameters validated from JSON scraper:', validated.WORK_BONUS);
        }

        return validated;
    }

    /**
     * Validate rent assistance data from JSON scraper
     * @param {Object} rentAssistance - Rent assistance data from scraper
     * @returns {Object} Validated rent assistance parameters
     */
    static _validateRentAssistance(rentAssistance) {
        const validated = {};
        
        // Validate maximum payment rates (per fortnight, from scraper)
        if (rentAssistance.maxRate && typeof rentAssistance.maxRate === 'object') {
            if (typeof rentAssistance.maxRate.single === 'number' && rentAssistance.maxRate.single > 0) {
                // Convert fortnightly to annual
                validated.SINGLE = {
                    MAX_PAYMENT: rentAssistance.maxRate.single * 26
                };
            }
            
            if (typeof rentAssistance.maxRate.couple === 'number' && rentAssistance.maxRate.couple > 0) {
                // Convert fortnightly to annual
                validated.COUPLE = {
                    MAX_PAYMENT: rentAssistance.maxRate.couple * 26
                };
            }
        }
        
        // Validate minimum rent thresholds (per fortnight, from scraper)
        if (rentAssistance.minRent && typeof rentAssistance.minRent === 'object') {
            if (typeof rentAssistance.minRent.single === 'number' && rentAssistance.minRent.single > 0) {
                // Convert fortnightly to annual
                validated.SINGLE = {
                    ...validated.SINGLE,
                    MIN_RENT: rentAssistance.minRent.single * 26
                };
            }
            
            if (typeof rentAssistance.minRent.couple === 'number' && rentAssistance.minRent.couple > 0) {
                // Convert fortnightly to annual
                validated.COUPLE = {
                    ...validated.COUPLE,
                    MIN_RENT: rentAssistance.minRent.couple * 26
                };
            }
        }
        
        // Validate taper rate (75 cents per dollar above threshold)
        if (typeof rentAssistance.taperRate === 'number' && rentAssistance.taperRate > 0 && rentAssistance.taperRate <= 1) {
            if (validated.SINGLE) {
                validated.SINGLE.TAPER_RATE = rentAssistance.taperRate;
            }
            if (validated.COUPLE) {
                validated.COUPLE.TAPER_RATE = rentAssistance.taperRate;
            }
        }
        
        console.info('✓ Rent Assistance rates validated from JSON scraper:', validated);
        
        return validated;
    }

    /**
     * Create fallback data from hardcoded configuration
     * @param {Date} targetDate - Target date
     * @returns {Object} Fallback configuration
     */
    static _createFallbackData(targetDate) {
        const fallbackData = { ...FinancialConfig };
        
        fallbackData._metadata = {
            source: 'hardcoded_fallback',
            loadDate: targetDate.toISOString(),
            loadTimestamp: Date.now(),
            version: '1.0'
        };

        return fallbackData;
    }

    /**
     * Cache data for future use
     * @param {Object} data - Data to cache
     * @param {string} dateKey - Date key
     */
    static _cacheData(data, dateKey) {
        this._cachedData = data;
        this._lastLoadDate = dateKey;
    }

    /**
     * Clear cached data
     */
    static clearCache() {
        this._cachedData = null;
        this._lastLoadDate = null;
    }

    /**
     * Get cached data info
     * @returns {Object} Cache information
     */
    static getCacheInfo() {
        return {
            hasCachedData: !!this._cachedData,
            lastLoadDate: this._lastLoadDate,
            cacheAge: this._cachedData ? Date.now() - this._cachedData._loadTimestamp : null,
            source: this._cachedData?._metadata?.source || 'none'
        };
    }

    /**
     * Load rates for a specific date range
     * @param {Date} startDate - Start date
     * @param {Date} endDate - End date
     * @returns {Promise<Object>} Rates for the date range
     */
    static async loadRatesForDateRange(startDate, endDate) {
        const rates = {};
        const currentDate = new Date(startDate);
        
        while (currentDate <= endDate) {
            const dateKey = currentDate.toISOString().split('T')[0];
            rates[dateKey] = await this.loadRates(currentDate);
            currentDate.setDate(currentDate.getDate() + 1);
        }
        
        return rates;
    }

    /**
     * Check if data is available for a specific date
     * @param {Date|string} date - Date to check
     * @returns {Promise<boolean>} True if data is available
     */
    static async isDataAvailable(date = new Date()) {
        try {
            const data = await this.loadRates(date);
            return !!data && data._metadata?.source !== 'error';
        } catch (error) {
            return false;
        }
    }
}

// Export default instance for easy importing
export default FinancialDataLoader;
